import { Loader2, RotateCcw, CheckCircle2, AlertCircle, UploadCloud } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import type { UploadJob } from '@/lib/uploadQueue';

interface Props<T> {
  jobs: readonly UploadJob<T>[];
  onRetryAll?: () => void;
  className?: string;
}

export function BulkUploadStatusStrip<T>({ jobs, onRetryAll, className }: Props<T>) {
  const uploading = jobs.filter((j) => j.status === 'uploading' || j.status === 'retrying').length;
  const done = jobs.filter((j) => j.status === 'done').length;
  const failed = jobs.filter((j) => j.status === 'failed').length;
  const queued = jobs.filter((j) => j.status === 'queued').length;

  if (jobs.length === 0) return null;

  return (
    <div
      className={cn(
        'rounded-xl border border-white/[0.06] bg-white/[0.02] px-3 py-2.5 text-xs flex flex-wrap items-center gap-2 tabular-nums',
        className
      )}
    >
      {uploading > 0 && (
        <span className="inline-flex items-center gap-1.5 text-active-gold">
          <Loader2 className="w-3.5 h-3.5 animate-spin" />
          {uploading} uploading
          {queued > 0 && <span className="text-foreground/50">({queued} queued)</span>}
        </span>
      )}
      {done > 0 && (
        <span className="inline-flex items-center gap-1.5 text-success">
          <CheckCircle2 className="w-3.5 h-3.5" />
          {done} done
        </span>
      )}
      {failed > 0 && (
        <span className="inline-flex items-center gap-1.5 text-destructive">
          <AlertCircle className="w-3.5 h-3.5" />
          {failed} failed
        </span>
      )}
      {uploading === 0 && done === 0 && failed === 0 && queued > 0 && (
        <span className="inline-flex items-center gap-1.5 text-muted-foreground">
          <UploadCloud className="w-3.5 h-3.5" />
          {queued} queued
        </span>
      )}
      {failed > 0 && onRetryAll && (
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={onRetryAll}
          className="ml-auto h-7 gap-1 rounded-full text-xs px-2.5 text-active-gold hover:text-active-gold hover:bg-active-gold/10"
        >
          <RotateCcw className="w-3.5 h-3.5" />
          Retry failed
        </Button>
      )}
    </div>
  );
}
